import HttpService from '@/services/http/http.service';
import {
    RealmlistsViewModel,
    RealmsViewModel,
    UpdateRealmsViewModel,
    AddRealmRequest,
    DeleteRealmRequest,
    EditRealmRequest,
    UpdateRealmNameRequest,
    UpdateRealmAllowedSecurityLevelRequest,
    RestartRealmRequest,
    GenericViewModel,
    DeleteFileRequest,
    DeleteMapFoldersRequest,
    UploadFileContentRequest,
    BuildStatusViewObject,
    CoreFilesViewModel,
    EditPatchRequest,
    RealmLogType,
    RealmLogViewObject
} from '@/types/apiServerContract';
import { AxiosProgressEvent, AxiosResponse } from 'axios';

const API_URL = 'realm/';

export class RealmApi {
    public static async GetRealms(): Promise<RealmlistsViewModel> {
        return HttpService.Get<RealmlistsViewModel>(API_URL + 'GetRealms');
    }

    public static async GetRealmInformations(): Promise<RealmsViewModel> {
        return HttpService.Get<RealmsViewModel>(API_URL + 'GetRealmInformations');
    }

    public static async AddRealm(request: AddRealmRequest): Promise<UpdateRealmsViewModel> {
        return HttpService.Post<UpdateRealmsViewModel>(API_URL + 'AddRealm', request);
    }

    public static async EditRealm(request: EditRealmRequest): Promise<UpdateRealmsViewModel> {
        return HttpService.Post<UpdateRealmsViewModel>(API_URL + 'EditRealm', request);
    }

    public static async DeleteRealm(request: DeleteRealmRequest): Promise<GenericViewModel<boolean>> {
        return HttpService.Post<GenericViewModel<boolean>>(API_URL + 'DeleteRealm', request);
    }

    public static async UpdateRealmName(request: UpdateRealmNameRequest): Promise<UpdateRealmsViewModel> {
        return HttpService.Post<UpdateRealmsViewModel>(API_URL + 'UpdateRealmName', request);
    }

    public static async UpdateAllowedSecurityLevel(request: UpdateRealmAllowedSecurityLevelRequest): Promise<UpdateRealmsViewModel> {
        return HttpService.Post<UpdateRealmsViewModel>(API_URL + 'UpdateAllowedSecurityLevel', request);
    }

    public static async ExecuteServerScript(scriptId: number): Promise<GenericViewModel<boolean>> {
        return HttpService.Get<GenericViewModel<boolean>>(API_URL + 'ExecuteServerScript', {
            scriptId: scriptId
        });
    }

    public static async RestartRealm(request: RestartRealmRequest): Promise<GenericViewModel<boolean>> {
        return HttpService.Post<GenericViewModel<boolean>>(API_URL + 'RestartRealm', request);
    }

    public static async GetPatchFiles(): Promise<GenericViewModel<string[]>> {
        return HttpService.Get<GenericViewModel<string[]>>(API_URL + 'GetPatchFiles');
    }

    public static async DeletePatchFile(request: DeleteFileRequest): Promise<GenericViewModel<boolean>> {
        return HttpService.Post<GenericViewModel<boolean>>(API_URL + 'DeletePatchFile', request);
    }

    public static async UpdatePatch(request: EditPatchRequest): Promise<GenericViewModel<boolean>> {
        return HttpService.Post<GenericViewModel<boolean>>(API_URL + 'UpdatePatch', request);
    }

    public static async GetLUAFiles(): Promise<CoreFilesViewModel> {
        return HttpService.Get<CoreFilesViewModel>(API_URL + 'GetLUAFiles');
    }

    public static async GetLUAFileContent(fileName: string): Promise<GenericViewModel<string>> {
        return HttpService.Get<GenericViewModel<string>>(API_URL + 'GetLUAFileContent', {
            fileName
        });
    }

    public static async GetSpellScriptFiles(): Promise<GenericViewModel<string[]>> {
        return HttpService.Get<GenericViewModel<string[]>>(API_URL + 'GetSpellScriptFiles');
    }

    public static async GetCoreFiles(): Promise<CoreFilesViewModel> {
        return HttpService.Get<CoreFilesViewModel>(API_URL + 'GetCoreFiles');
    }

    public static async CreateLUAFolder(directoryPath: string): Promise<CoreFilesViewModel> {
        return HttpService.Get<CoreFilesViewModel>(API_URL + 'CreateLUAFolder', {
            directoryPath
        });
    }

    public static async CreateCoreFolder(directoryPath: string): Promise<CoreFilesViewModel> {
        return HttpService.Get<CoreFilesViewModel>(API_URL + 'CreateCoreFolder', {
            directoryPath
        });
    }

    public static async CreateLUAFile(filePath: string): Promise<CoreFilesViewModel> {
        return HttpService.Get<CoreFilesViewModel>(API_URL + 'CreateLUAFile', {
            filePath
        });
    }

    public static async GetFileContent(fileName: string): Promise<GenericViewModel<string>> {
        return HttpService.Get<GenericViewModel<string>>(API_URL + 'GetFileContent', {
            fileName
        });
    }

    public static async CreateCoreFile(filePath: string): Promise<CoreFilesViewModel> {
        return HttpService.Get<CoreFilesViewModel>(API_URL + 'CreateCoreFile', {
            filePath
        });
    }

    public static async UploadFileContent(request: UploadFileContentRequest): Promise<GenericViewModel<boolean>> {
        return HttpService.Post<GenericViewModel<boolean>>(API_URL + 'UploadFileContent', request);
    }

    public static async UploadLuaFileContent(request: UploadFileContentRequest): Promise<GenericViewModel<boolean>> {
        return HttpService.Post<GenericViewModel<boolean>>(API_URL + 'UploadLuaFileContent', request);
    }

    public static async DeleteLUAFile(request: DeleteFileRequest): Promise<GenericViewModel<boolean>> {
        return HttpService.Post<GenericViewModel<boolean>>(API_URL + 'DeleteLUAFile', request);
    }

    public static async DeleteCoreFile(request: DeleteFileRequest): Promise<GenericViewModel<boolean>> {
        return HttpService.Post<GenericViewModel<boolean>>(API_URL + 'DeleteCoreFile', request);
    }

    public static async DeleteWoWExe(request: DeleteFileRequest): Promise<GenericViewModel<boolean>> {
        return HttpService.Post<GenericViewModel<boolean>>(API_URL + 'DeleteWoWExe', request);
    }

    public static async DeleteMapFolders(request: DeleteMapFoldersRequest): Promise<GenericViewModel<boolean>> {
        return HttpService.Post<GenericViewModel<boolean>>(API_URL + 'DeleteMapFolders', request);
    }

    public static async GetWoWExeFile(): Promise<GenericViewModel<string>> {
        return HttpService.Get<GenericViewModel<string>>(API_URL + 'GetWoWExeFile');
    }

    public static async InitiateBuild(): Promise<GenericViewModel<boolean>> {
        return HttpService.Post<GenericViewModel<boolean>>(API_URL + 'InitiateBuild');
    }

    public static async InitiateFullRebuild(): Promise<GenericViewModel<boolean>> {
        return HttpService.Post<GenericViewModel<boolean>>(API_URL + 'InitiateFullRebuild');
    }

    public static async CancelBuild(): Promise<GenericViewModel<boolean>> {
        return HttpService.Post<GenericViewModel<boolean>>(API_URL + 'CancelBuild');
    }

    public static async GetBuildStatus(): Promise<BuildStatusViewObject> {
        return HttpService.Get<BuildStatusViewObject>(API_URL + 'GetBuildStatus');
    }

    public static async GetServerStatus(): Promise<GenericViewModel<string>> {
        return HttpService.Get<GenericViewModel<string>>(API_URL + 'GetServerStatus', undefined, undefined, false, true);
    }

    public static async GetElunaStatus(): Promise<GenericViewModel<string>> {
        return HttpService.Get<GenericViewModel<string>>(API_URL + 'GetElunaStatus', undefined, undefined, false, true);
    }

    public static async GetDBErrorsStatus(): Promise<GenericViewModel<string>> {
        return HttpService.Get<GenericViewModel<string>>(API_URL + 'GetDBErrorsStatus', undefined, undefined, false, true);
    }

    public static async getRealmLogs(type: RealmLogType): Promise<RealmLogViewObject[]> {
        return HttpService.Get<RealmLogViewObject[]>(API_URL + 'GetRealmLogs', {
            realmLogType: type
        }, undefined, true);
    }

    public static async GetDbcFiles(): Promise<GenericViewModel<string[]>> {
        return HttpService.Get<GenericViewModel<string[]>>(API_URL + 'GetDbcFiles');
    }

    public static async DownloadDbc(fileName: string): Promise<AxiosResponse<Blob>> {
        return HttpService.Get<AxiosResponse<Blob>>(API_URL + 'DownloadDbc', { fileName }, { responseType: 'blob' }, true, true);
    }

    public static async DownloadAllDbc(): Promise<AxiosResponse<Blob>> {
        return HttpService.Get<AxiosResponse<Blob>>(API_URL + 'DownloadAllDbc', undefined, { responseType: 'blob' }, true, true);
    }

    public static async UploadDBC(files: File[], callback: (event: AxiosProgressEvent) => void) {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));

        const result = await HttpService.Put<GenericViewModel<boolean>>(API_URL + 'UploadDBC', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (event: AxiosProgressEvent) => callback(event)
        });
        return result;
    }

    public static async UploadWoWExe(files: any, callback: (event: AxiosProgressEvent) => void) {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));

        const result = await HttpService.Put<GenericViewModel<boolean>>(API_URL + 'UploadWoWExe', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (event: AxiosProgressEvent) => callback(event)
        });
        return result;
    }

    public static async UploadPatch(files: any[], callback: (event: AxiosProgressEvent) => void) {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));

        const result = await HttpService.Put<GenericViewModel<boolean>>(API_URL + 'UploadPatch', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (event: AxiosProgressEvent) => callback(event)
        });
        return result;
    }

    public static async UploadMaps(files: File[], callback: (event: AxiosProgressEvent) => void) {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));

        const result = await HttpService.Put<GenericViewModel<boolean>>(API_URL + 'UploadMaps', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (event: AxiosProgressEvent) => callback(event)
        });
        return result;
    }

    public static async UploadLuaFiles(files: any[], callback: (event: AxiosProgressEvent) => void) {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));

        const result = await HttpService.Put<GenericViewModel<boolean>>(API_URL + 'UploadLuaFiles', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (event: AxiosProgressEvent) => callback(event)
        });
        return result;
    }

    public static async GetWorldServerConfigFileContent(): Promise<GenericViewModel<string>> {
        return HttpService.Get<GenericViewModel<string>>(API_URL + 'GetWorldServerConfigFileContent');
    }

    public static async UploadWorldServerConfigFileContent(request: UploadFileContentRequest): Promise<GenericViewModel<boolean>> {
        return HttpService.Post<GenericViewModel<boolean>>(API_URL + 'UploadWorldServerConfigFileContent', request);
    }
}
